import type { ReceivedInvite } from '@nexo/types';
import { Avatar, Button, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Mail, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { Logo } from '../../components/Logo';
import { rememberedTeamId } from '../../layouts/TeamShell';
import { api } from '../../lib/api';
import { keys, useMyInvites, useTeams } from '../../lib/queries';
import { FullScreenSpinner } from '../auth/RequireAuth';
import { useMe } from '../auth/AuthProvider';
import { CreateTeamForm } from './CreateTeamDialog';
import { errorMessage } from '../../lib/forms';

/** `/app`: jump straight into the last (or first) team, or show the home page. */
export function AppIndex() {
  const teams = useTeams();
  if (!teams.data) return <FullScreenSpinner />;
  const target = teams.data.find((t) => t.id === rememberedTeamId()) ?? teams.data[0];
  return <Navigate to={target ? `/app/t/${target.id}` : '/app/home'} replace />;
}

export function HomePage() {
  const me = useMe();
  const teams = useTeams();
  const invites = useMyInvites();
  const { t } = useTranslation();

  if (teams.isPending) return <FullScreenSpinner />;
  const hasTeams = (teams.data?.length ?? 0) > 0;

  return (
    <div className="min-h-full overflow-y-auto bg-canvas">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
        <Logo />
        <Link
          to="/app/profile"
          className="flex items-center gap-2.5 rounded-full py-1 pr-1 pl-3 text-sm text-muted hover:bg-surface hover:text-text"
        >
          {me.displayName}
          <Avatar id={me.id} name={me.displayName} src={me.avatarUrl} size="sm" />
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-5 pt-6 pb-16">
        <h1 className="text-3xl font-semibold tracking-tight">
          {t('Hola, {{name}} 👋', { name: me.displayName.split(' ')[0] })}
        </h1>
        <p className="mt-1.5 text-muted">
          {hasTeams
            ? t('Elige un equipo para continuar o crea uno nuevo.')
            : t('Crea tu primer equipo o acepta una invitación para empezar.')}
        </p>

        {invites.data && invites.data.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 text-[11px] font-semibold tracking-wider text-subtle uppercase">
              {t('Invitaciones pendientes')}
            </h2>
            <div className="flex flex-col gap-2">
              {invites.data.map((invite) => (
                <InviteCard key={invite.id} invite={invite} />
              ))}
            </div>
          </section>
        )}

        {hasTeams && (
          <section className="mt-8">
            <h2 className="mb-3 text-[11px] font-semibold tracking-wider text-subtle uppercase">
              {t('Tus equipos')}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {teams.data?.map((team) => (
                <Link
                  key={team.id}
                  to={`/app/t/${team.id}`}
                  className="group flex items-center gap-3.5 rounded-2xl border border-border bg-surface p-4 shadow-panel transition-[border-color,box-shadow] hover:border-border-strong hover:shadow-float"
                >
                  <Avatar
                    id={team.id}
                    name={team.name}
                    shape="square"
                    size="md"
                    className="[&>span]:size-11 [&>span]:rounded-xl"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{team.name}</span>
                    <span className="block text-xs text-muted">
                      {team.memberCount === 1
                        ? t('1 miembro')
                        : t('{{count}} miembros', { count: team.memberCount })}{' '}
                      · {t(roleLabel[team.role])}
                    </span>
                  </span>
                  <ChevronRight className="size-4 text-subtle transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8 rounded-2xl border border-border bg-surface p-6 shadow-panel">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <Plus className="size-5" />
            </span>
            <div>
              <h2 className="font-semibold">
                {hasTeams ? t('Crear otro equipo') : t('Crea tu equipo')}
              </h2>
              <p className="text-sm text-muted">
                {t('Tendrás un canal #general listo para empezar.')}
              </p>
            </div>
          </div>
          <CreateTeamForm autoFocus={!hasTeams} />
        </section>
      </main>
    </div>
  );
}

const roleLabel = { owner: 'Propietario', admin: 'Admin', member: 'Miembro' } as const;

export function InviteCard({
  invite,
  onDone,
}: {
  invite: ReceivedInvite;
  onDone?: (teamId: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.myInvites }),
      queryClient.invalidateQueries({ queryKey: keys.teams }),
    ]);

  const accept = useMutation({
    mutationFn: () => api.invites.accept(invite.id),
    onSuccess: async (team) => {
      await refresh();
      toast({ title: t('Te has unido a {{team}}', { team: team.name }), tone: 'success' });
      onDone?.(team.id);
    },
    onError: (err) =>
      toast({ title: t('No se pudo aceptar'), description: errorMessage(err), tone: 'error' }),
  });
  const decline = useMutation({
    mutationFn: () => api.invites.decline(invite.id),
    onSuccess: async () => {
      await refresh();
      onDone?.(null);
    },
  });

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-accent/25 bg-surface p-4 shadow-panel sm:flex-row sm:items-center">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
        <Mail className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium">{invite.teamName}</p>
        <p className="text-sm text-muted">
          {t('Te invita {{name}}', { name: invite.invitedBy.displayName })}
        </p>
      </div>
      {invite.forCurrentUser ? (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            loading={decline.isPending}
            onClick={() => decline.mutate()}
          >
            {t('Rechazar')}
          </Button>
          <Button size="sm" loading={accept.isPending} onClick={() => accept.mutate()}>
            {t('Unirme')}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-danger">
          {t('Esta invitación es para {{email}}', { email: invite.email })}
        </p>
      )}
    </div>
  );
}
